import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { getPreferences, savePreferences, resetToDefaults } from '../services/columnPreferencesService';

const ColumnPreferencesContext = createContext(null);

export function ColumnPreferencesProvider({ children }) {
  // State: { [entityType]: columns[] }
  const [columnConfigs, setColumnConfigs] = useState({});
  const [loading, setLoading] = useState({});
  const saveTimers = useRef({});

  /**
   * Load column preferences for an entity type (if not already loaded).
   */
  const loadColumns = useCallback(async (entityType) => {
    if (columnConfigs[entityType]) return columnConfigs[entityType];
    setLoading(prev => ({ ...prev, [entityType]: true }));
    try {
      const columns = await getPreferences(entityType);
      setColumnConfigs(prev => ({ ...prev, [entityType]: columns }));
      return columns;
    } finally {
      setLoading(prev => ({ ...prev, [entityType]: false }));
    }
  }, [columnConfigs]);

  /**
   * Debounced save to avoid excessive API calls.
   */
  const debouncedSave = useCallback((entityType, columns) => {
    if (saveTimers.current[entityType]) {
      clearTimeout(saveTimers.current[entityType]);
    }
    saveTimers.current[entityType] = setTimeout(() => {
      savePreferences(entityType, columns);
    }, 500);
  }, []);

  /**
   * Set the full column config for an entity type (and persist).
   */
  const setColumnConfig = useCallback((entityType, columns) => {
    setColumnConfigs(prev => ({ ...prev, [entityType]: columns }));
    debouncedSave(entityType, columns);
  }, [debouncedSave]);

  /**
   * Toggle visibility of a single column.
   */
  const toggleColumnVisibility = useCallback((entityType, columnKey) => {
    setColumnConfigs(prev => {
      const columns = prev[entityType];
      if (!columns) return prev;
      const updated = columns.map(col => {
        if (col.key === columnKey && !col.locked) {
          return { ...col, visible: !col.visible };
        }
        return col;
      });
      // Ensure at least one column is visible
      const visibleCount = updated.filter(c => c.visible).length;
      if (visibleCount === 0) return prev;
      debouncedSave(entityType, updated);
      return { ...prev, [entityType]: updated };
    });
  }, [debouncedSave]);

  /**
   * Reorder columns by providing the full new column array.
   */
  const reorderColumns = useCallback((entityType, columns) => {
    const reordered = columns.map((col, idx) => ({ ...col, order: idx }));
    setColumnConfigs(prev => ({ ...prev, [entityType]: reordered }));
    debouncedSave(entityType, reordered);
  }, [debouncedSave]);

  /**
   * Reset columns to defaults for an entity type.
   */
  const resetColumns = useCallback(async (entityType) => {
    const defaults = await resetToDefaults(entityType);
    setColumnConfigs(prev => ({ ...prev, [entityType]: defaults }));
    return defaults;
  }, []);

  /**
   * Get only visible columns, sorted by order.
   */
  const getVisibleColumns = useCallback((entityType) => {
    const columns = columnConfigs[entityType];
    if (!columns) return [];
    return [...columns]
      .filter(c => c.visible)
      .sort((a, b) => a.order - b.order);
  }, [columnConfigs]);

  /**
   * Get all columns (visible + hidden), sorted by order.
   */
  const getAllColumns = useCallback((entityType) => {
    const columns = columnConfigs[entityType];
    if (!columns) return [];
    return [...columns].sort((a, b) => a.order - b.order);
  }, [columnConfigs]);

  const value = {
    columnConfigs,
    loading,
    loadColumns,
    setColumnConfig,
    toggleColumnVisibility,
    reorderColumns,
    resetColumns,
    getVisibleColumns,
    getAllColumns,
  };

  return (
    <ColumnPreferencesContext.Provider value={value}>
      {children}
    </ColumnPreferencesContext.Provider>
  );
}

export function useColumnPreferences() {
  const ctx = useContext(ColumnPreferencesContext);
  if (!ctx) {
    throw new Error('useColumnPreferences must be used within a ColumnPreferencesProvider');
  }
  return ctx;
}
